import { useState, useCallback, useEffect, lazy, Suspense } from 'react';
import { useSynligtIntervall } from '../hooks/useSynligtIntervall';
import { API_BASE, fetchWithAuth, fetchTasks, type Task } from '../api';
import { TaskDetail } from '../components/TaskDetail';
import { BackendAlexChat } from '../components/BackendAlexChat';
import {
  MessageCircle,
  ListTodo,
  Puzzle,
  Wallet,
  Zap,
  Clock,
  Search,
  Globe,
  Shield,
  BarChart3,
  FileText,
  X,
  Activity,
  Layers,
  ArrowUpRight,
  Database,
  Plug,
  ChevronDown,
  PanelLeft,
} from 'lucide-react';
import { AlexChat } from '../components/AlexChat';
import { ThreadSidebar } from '../components/chat/ThreadSidebar';
import { ThreadMemoryPanel } from '../components/chat/ThreadMemoryPanel';
const STATUS_ORD: Record<string, string> = {
  created: 'Skapad', assigned: 'Tilldelad', in_progress: 'Pågår',
  review: 'Granskas', completed: 'Klar', failed: 'Misslyckad',
};

const CostCenter = lazy(() => import('./CostCenter').then(m => ({ default: m.CostCenter })));
import { MAIN_SESSION, useAlexGateway } from '../gateway/AlexGateway';
import { CharacterSheet } from '../components/CharacterSheet';

/* ─── Types ─── */
interface Skill {
  id: string;
  name: string;
  description: string;
  category: 'search' | 'automation' | 'content' | 'monitor' | 'system' | 'data' | 'integration';
  source?: 'workspace' | 'standalone' | 'subagent' | 'mcp';
}

type SidebarTab = 'chat' | 'tasks' | 'skills' | 'costs';

/* Gatewayn kategoriserar inte skills — den har namn och beskrivning. Enkel
   nyckelordsgissning så ikonerna i listan blir rimliga; 'system' är default
   och betyder "okategoriserad", inte "systemskill". */
function categorizeSkill(name: string): Skill['category'] {
  const n = name.toLowerCase();
  if (/search|lookup|research|find|scrape|crawl/.test(n)) return 'search';
  if (/mail|calendar|schedul|pipeline|batch|auto|poller|cron/.test(n)) return 'automation';
  if (/content|writer|post|copy|caption|video|image|design|ad-/.test(n)) return 'content';
  if (/monitor|watch|check|health|drift|report|analytic/.test(n)) return 'monitor';
  if (/db|sql|supabase|data|csv|sheet|crm/.test(n)) return 'data';
  if (/api|apify|apollo|slack|whatsapp|notion|github|connect/.test(n)) return 'integration';
  return 'system';
}

/* ─── Config ─── */
const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  search: <Globe size={14} />,
  automation: <Zap size={14} />,
  content: <FileText size={14} />,
  monitor: <BarChart3 size={14} />,
  system: <Shield size={14} />,
  data: <Database size={14} />,
  integration: <Plug size={14} />,
};

const CATEGORY_LABELS: Record<string, string> = {
  search: 'Sök & Research',
  automation: 'Automation',
  content: 'Innehåll',
  monitor: 'Övervakning',
  system: 'System',
  data: 'Data & Lagring',
  integration: 'Integrationer',
};

type SourceFilter = 'all' | 'skills' | 'agents' | 'mcp';

// SCC-38: alltid via API_BASE (relativ i prod) + auth — aldrig hårdkodad localhost.
const SKILLS_API = `${API_BASE}/skills`;

export default function AlexView() {
  const [activeTab, setActiveTab] = useState<SidebarTab>('chat');
  /* Sidofältet är en utfällbar låda under 1100px (se alex.css). Tidigare var
     det bara bortgömt med display: none, vilket tog bort trådlistan och
     "Ny tråd" helt på telefon. */
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState<Skill | null>(null);
  const [skillSearch, setSkillSearch] = useState('');
  const [skills, setSkills] = useState<Skill[]>([]);
  // false = backendet kan inte läsa skills-katalogen (den bor på maskinen där
  // OpenClaw kör). Visa "?" i stället för "0" — noll är ett påstående, och det
  // påståendet var falskt.
  const [skillsAvailable, setSkillsAvailable] = useState(true);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  // Varje val i lådan stänger den — annars ligger den kvar över det man valde.
  const openTab = useCallback((tab: SidebarTab) => {
    setActiveTab(tab);
    setSidebarOpen(false);
  }, []);

  // Esc stänger lådan, som vilken overlay som helst.
  useEffect(() => {
    if (!sidebarOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setSidebarOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sidebarOpen]);

  const toggleSection = useCallback((section: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  }, []);

  const gateway = useAlexGateway();

  /* ─── Vilken Alex chattflik visar ───
     'gateway' = huvudagenten (skills, subagenter, huvudtråden som delas med
     WhatsApp). 'cloud' = serverside-Alex via /api/v1/chat, egen tråd.

     Den gamla SCC-38-fallbacken bytte hit AUTOMATISKT efter fyra sekunders
     tystnad från gatewayn. Att den växlingen togs bort var rätt — man kunde
     sitta och prata med fel Alex utan att veta om det. Men gatewayn är
     tailnet-bunden (loopback på VPS:en, `tailscale serve` framför), så varje
     enhet utan tailnet ser "Ej ansluten" även när Alex jobbar för fullt i
     Helsingfors. Moln-Alex är det enda som svarar då, så kapaciteten behövs.
     Den ligger kvar, men bakom ett klick: värdet ändras bara av användaren,
     aldrig av anslutningsstatus. */
  const [chatSource, setChatSource] = useState<'gateway' | 'cloud'>('gateway');

  /* ─── Skills: gatewayn först, backendet som fallback ───
     Katalogen bor på VPS:en där OpenClaw kör. Molnbackendet kan inte läsa
     den och svarade därför 0 — därav "Capabilities 0" med grön gateway.
     WebSocket:en frontenden redan har öppen kan läsa den, så den får gå först
     och backendet blir fallback för lokal körning utan gateway. */
  const [backendSkills, setBackendSkills] = useState<Skill[]>([]);
  const [backendSkillsAvailable, setBackendSkillsAvailable] = useState(true);

  useEffect(() => {
    fetchWithAuth(SKILLS_API)
      .then(r => r.json())
      .then(data => {
        setBackendSkills(data.skills || []);
        setBackendSkillsAvailable(data.available !== false);
      })
      .catch(err => console.error('Failed to fetch skills:', err));
  }, []);

  /* ─── Subagenter och MCP-servrar ───
     Gatewayns skills.status kan bara skills; den vet ingenting om subagenter
     eller MCP-servrar och sätter aldrig `source`. Därför stod Agenter och MCP
     på 0 hur många skills som än laddades — filtren letade efter ett fält som
     aldrig fanns.
     `/api/v1/skills-aggregator` läser subagentkatalogerna och mcp_config.json
     och sätter source korrekt, men den var aldrig inkopplad i frontenden. Den
     läser filer på VPS:en där OpenClaw kör, så på Render svarar den tomt och
     flikarna göms i stället för att visa nollor i evighet. */
  const [extraSkills, setExtraSkills] = useState<Skill[]>([]);

  /* ─── Aktiva uppgifter ───
     Panelen var hårdkodad tom: den sa "Inga aktiva uppgifter just nu" oavsett
     verklighet, medan statusraden längst ned räknade riktiga rader. Därav
     motsägelsen "1 aktiva uppgifter" mot en tom vy. */
  const [uppgifter, setUppgifter] = useState<Task[]>([]);
  const [uppgifterLaddar, setUppgifterLaddar] = useState(true);
  const [valdUppgift, setValdUppgift] = useState<Task | null>(null);

  const hamtaUppgifter = useCallback(async () => {
    try {
      const grupper = await Promise.all([
        fetchTasks({ status: 'in_progress', limit: 50 }),
        fetchTasks({ status: 'assigned', limit: 50 }),
        fetchTasks({ status: 'review', limit: 50 }),
      ]);
      const alla = grupper.flat();
      alla.sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at));
      setUppgifter(alla);
    } catch {
      setUppgifter([]);
    } finally {
      setUppgifterLaddar(false);
    }
  }, []);

  useEffect(() => { void hamtaUppgifter(); }, [hamtaUppgifter]);
  // Uppgiftslistan var 30:e sekund, men bara när fliken syns.
  useSynligtIntervall(() => { void hamtaUppgifter(); }, 30000);

  useEffect(() => {
    fetchWithAuth(`${API_BASE}/skills-aggregator`)
      .then(r => (r.ok ? r.json() : { skills: [] }))
      .then(data => {
        const rows = (data.skills || []) as Array<Record<string, unknown>>;
        setExtraSkills(rows
          .filter(s => s.source === 'subagent' || s.source === 'mcp')
          .map(s => ({
            id: String(s.id ?? s.name ?? ''),
            name: String(s.name ?? ''),
            description: String(s.description ?? ''),
            category: categorizeSkill(String(s.name ?? '')),
            source: s.source as Skill['source'],
          })));
      })
      .catch(() => setExtraSkills([]));
  }, []);

  useEffect(() => {
    if (gateway.skills.length > 0) {
      setSkills([...gateway.skills.map(g => ({
        id: g.name,
        name: g.name,
        description: g.description || '',
        category: categorizeSkill(g.name),
        source: g.bundled ? ('standalone' as const) : ('workspace' as const),
      })), ...extraSkills]);
      setSkillsAvailable(true);
    } else {
      setSkills([...backendSkills, ...extraSkills]);
      setSkillsAvailable(backendSkillsAvailable);
    }
  }, [gateway.skills, backendSkills, backendSkillsAvailable, extraSkills]);

  /* ─── Rollformulär (ersätter gamla Rollfiler-modalen som läste filer
     från disk — de finns bara på VPS:en där OpenClaw kör, aldrig på Render) ─── */
  const [sheetOpen, setSheetOpen] = useState(false);

  /* ─── Derived: filter + group ─── */
  const skillsBySource = (filter: SourceFilter) => {
    if (filter === 'skills') return skills.filter(s => s.source === 'workspace' || s.source === 'standalone');
    if (filter === 'agents') return skills.filter(s => s.source === 'subagent');
    if (filter === 'mcp') return skills.filter(s => s.source === 'mcp');
    return skills;
  };

  const sourceCounts = {
    all: skills.length,
    skills: skills.filter(s => s.source === 'workspace' || s.source === 'standalone').length,
    agents: skills.filter(s => s.source === 'subagent').length,
    mcp: skills.filter(s => s.source === 'mcp').length,
  };

  const filteredSkills = skillsBySource(sourceFilter).filter(s =>
    (s.name || '').toLowerCase().includes(skillSearch.toLowerCase()) ||
    (s.description || '').toLowerCase().includes(skillSearch.toLowerCase())
  );

  // Group by category for display
  const categoryGroups = Object.keys(CATEGORY_LABELS)
    .map(cat => ({
      key: cat,
      label: CATEGORY_LABELS[cat],
      icon: CATEGORY_ICONS[cat],
      items: filteredSkills.filter(s => s.category === cat),
    }))
    .filter(g => g.items.length > 0);

  const handleSkillClick = useCallback((skill: Skill) => {
    setSelectedSkill(prev => prev?.id === skill.id ? null : skill);
  }, []);

  const handleTaskCreated = useCallback(() => {
    console.log('[AlexView] Task created');
  }, []);

  /* ─── Avatar State ─── */
  const currentAvatar = '/avatars/alex.png';

  return (
    <div className={`alex-view${sidebarOpen ? ' alex-sidebar-open' : ''}`}>
      {/* Mörkläggning bakom lådan — klick utanför stänger. */}
      <div
        className="alex-sidebar-scrim"
        onClick={() => setSidebarOpen(false)}
        aria-hidden="true"
      />

      {/* ─── Left Sidebar ─── */}
      <aside className="alex-sidebar">
        {/* Alex Profile Card with Avatar */}
        <div className="alex-profile-card">
          <button
            className="alex-profile-avatar alex-role-trigger"
            onClick={() => setSheetOpen(true)}
            title="Visa rollformulär"
          >
            <div className="alex-avatar-ring" />
            <img 
              src={currentAvatar}
              alt="Alex Avatar" 
              className="alex-avatar-image"
              onError={(e) => {
                // Fallback to Bot icon if image fails
                e.currentTarget.style.display = 'none';
              }}
            />
          </button>
          <div className="alex-profile-info">
            <span className="alex-profile-name">Alex</span>
            <span className="alex-profile-role">AI Assistant</span>
          </div>
          <div className="alex-profile-stats">
            <div className="alex-stat">
              <Activity size={10} />
              <span>
                {chatSource === 'cloud'
                  ? 'Moln-Alex'
                  : gateway.status === 'connected'
                    ? 'Online'
                    : 'Ej ansluten'}
              </span>
            </div>
            <div className="alex-stat">
              <Layers size={10} />
              <span title={skillsAvailable ? undefined : 'Skills-katalogen bor på VPS:en där OpenClaw kör. Anslut till tailnetet för att se den.'}>
                {skillsAvailable ? `${skills.length} skills` : 'skills: ej läsbart här'}
              </span>
            </div>
          </div>
        </div>

        {/* ── Collapsible Sidebar Sections ── */}
        <nav className="alex-sidebar-sections">
          {/* ── Konversationer Section ── */}
          <div className="alex-section">
            <button
              className={`alex-section-header ${collapsedSections.has('threads') ? 'collapsed' : ''}`}
              onClick={() => toggleSection('threads')}
            >
              <ChevronDown size={14} className="alex-section-chevron" />
              <span>Konversationer</span>
            </button>
            {!collapsedSections.has('threads') && (
              <div className="alex-section-items">
                <button
                  className={`alex-section-item ${activeTab === 'chat' ? 'active' : ''}`}
                  onClick={() => openTab('chat')}
                >
                  <MessageCircle size={13} />
                  <span>Aktiv chatt</span>
                </button>
                <p className="alex-thread-note">
                  {chatSource === 'cloud'
                    ? 'Du pratar med moln-Alex i en egen tråd. Trådarna nedan hör till gatewayn.'
                    : gateway.sessionKey === MAIN_SESSION
                      ? 'Huvudtråden delas med WhatsApp och den flytande panelen.'
                      : 'Separat tråd. WhatsApp fortsätter i huvudtråden.'}
                </p>
                <button className="alex-section-item" onClick={() => {
                  setChatSource('gateway'); gateway.setSessionKey(MAIN_SESSION); openTab('chat');
                }}>Alex — huvudtråd / WhatsApp</button>
                <ThreadSidebar
                  sessions={gateway.sessions}
                  activeSessionKey={gateway.sessionKey}
                  threadPreviews={gateway.threadPreviews}
                  onSelectSession={(key) => { setChatSource('gateway'); gateway.setSessionKey(key); openTab('chat'); }}
                  /* Trådar skapas av gatewayn. Utan anslutning returnerade knappen
                     tyst — ett klick som inte gjorde något och inte sa varför. */
                  newThreadDisabledReason={gateway.status !== 'connected'
                    ? 'Kräver anslutning till gatewayn'
                    : undefined}
                  onNewThread={() => {
                    setChatSource('gateway');
                    openTab('chat');
                    gateway.createNewSession().catch(console.error);
                  }}
                />
              </div>
            )}
          </div>

          {/* ── Uppgifter Section ── */}
          <div className="alex-section">
            <button
              className={`alex-section-header ${collapsedSections.has('tasks') ? 'collapsed' : ''}`}
              onClick={() => toggleSection('tasks')}
            >
              <ChevronDown size={14} className="alex-section-chevron" />
              <span>Uppgifter</span>
            </button>
            {!collapsedSections.has('tasks') && (
              <div className="alex-section-items">
                <button
                  className={`alex-section-item ${activeTab === 'tasks' ? 'active' : ''}`}
                  onClick={() => openTab('tasks')}
                >
                  <ListTodo size={13} />
                  <span>Aktiva uppgifter</span>
                </button>
              </div>
            )}
          </div>

          {/* ── Agent Section ── */}
          <div className="alex-section">
            <button
              className={`alex-section-header ${collapsedSections.has('agent') ? 'collapsed' : ''}`}
              onClick={() => toggleSection('agent')}
            >
              <ChevronDown size={14} className="alex-section-chevron" />
              <span>Agent</span>
            </button>
            {!collapsedSections.has('agent') && (
              <div className="alex-section-items">
                <button
                  className={`alex-section-item ${activeTab === 'skills' ? 'active' : ''}`}
                  onClick={() => openTab('skills')}
                >
                  <Puzzle size={13} />
                  <span>Capabilities</span>
                  <span className="alex-section-count"
                        title={skillsAvailable ? undefined : 'Kan inte läsas från molnbackendet — kräver gateway-anslutning.'}>
                    {skillsAvailable ? skills.length : '?'}
                  </span>
                </button>
                <button
                  className={`alex-section-item ${activeTab === 'costs' ? 'active' : ''}`}
                  onClick={() => openTab('costs')}
                >
                  <Wallet size={13} />
                  <span>Kostnader</span>
                </button>
              </div>
            )}
          </div>

          {/* ── Minne Section ── */}
          <div className="alex-section">
            <button
              className={`alex-section-header ${collapsedSections.has('memory') ? 'collapsed' : ''}`}
              onClick={() => toggleSection('memory')}
            >
              <ChevronDown size={14} className="alex-section-chevron" />
              <span>Alex Minne</span>
            </button>
            {!collapsedSections.has('memory') && (
              <div className="alex-section-items">
                <ThreadMemoryPanel
                  memoryEntries={gateway.memoryEntries}
                  onSearch={gateway.searchMemory}
                />
              </div>
            )}
          </div>

        </nav>
      </aside>

      {/* ─── Main Content Area ─── */}
      <section className="alex-content">
        {/* Bara synlig under 1100px — desktop har sidofältet permanent. */}
        <button
          type="button"
          className="alex-sidebar-toggle"
          onClick={() => setSidebarOpen(true)}
          aria-expanded={sidebarOpen}
          aria-label="Visa trådar och paneler"
        >
          <PanelLeft size={14} />
          <span>Trådar</span>
          {gateway.sessions.length > 0 && (
            <span className="alex-section-count">{gateway.sessions.length}</span>
          )}
        </button>

        {activeTab === 'chat' && (
          chatSource === 'cloud'
            ? <BackendAlexChat
                onBackToGateway={() => setChatSource('gateway')}
                gatewayOnline={gateway.status === 'connected'}
              />
            : <AlexChat
                onTaskCreated={handleTaskCreated}
                gateway={gateway}
                onUseCloudAlex={() => setChatSource('cloud')}
              />
        )}

        {activeTab === 'tasks' && (
          <div className="alex-panel-glass">
            <div className="alex-panel-header">
              <ListTodo size={16} />
              <span>Aktiva uppgifter</span>
              {uppgifter.length > 0 && (
                <span className="skills-category-count">{uppgifter.length}</span>
              )}
            </div>

            {uppgifterLaddar ? (
              <div className="alex-panel-empty"><p>Laddar…</p></div>
            ) : uppgifter.length === 0 ? (
              <div className="alex-panel-empty">
                <Clock size={24} strokeWidth={1.5} />
                <p>Inga pågående uppgifter</p>
              </div>
            ) : (
              <div className="alex-task-list">
                {uppgifter.map(t => {
                  const dagar = Math.floor((Date.now() - Date.parse(t.created_at)) / 864e5);
                  // En uppgift som stått i samma läge i över ett dygn arbetar inte,
                  // den har fastnat. Statusraden räknade den som aktiv, vilket är
                  // hur "Producera Paket 1" kunde stå som pågående i 47 dagar.
                  const fast = dagar >= 1;
                  return (
                    <button
                      key={t.id}
                      className="alex-task-row"
                      onClick={() => setValdUppgift(t)}
                      title="Öppna uppgiften"
                    >
                      <span className={`alex-task-status alex-task-status--${t.status}`}>
                        {STATUS_ORD[t.status] ?? t.status}
                      </span>
                      <span className="alex-task-title">{t.title}</span>
                      <span className="alex-task-meta">
                        {t.executor}
                        {' · '}
                        <span style={fast ? { color: '#FF9F0A' } : undefined}>
                          {dagar === 0 ? 'idag' : dagar === 1 ? '1 dag' : `${dagar} dagar`}
                          {fast ? ' — fastnat?' : ''}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {valdUppgift && (
          <TaskDetail
            task={valdUppgift}
            onClose={() => setValdUppgift(null)}
            onTaskUpdated={t => setUppgifter(rader => rader.map(r => (r.id === t.id ? t : r)))}
          />
        )}

        {activeTab === 'skills' && (
          <div className="alex-skills-panel">
            {/* ─── Segmented Control ─── */}
            <div className="skills-segment-bar">
              {(['all', 'skills', 'agents', 'mcp'] as SourceFilter[])
                // Göm Agenter och MCP när de är tomma. Källan finns bara på maskinen
                // där OpenClaw kör, och en flik som alltid säger 0 ser ut som en bugg.
                .filter(seg => (seg === 'agents' ? sourceCounts.agents > 0 : seg === 'mcp' ? sourceCounts.mcp > 0 : true))
                .map(seg => (
                <button
                  key={seg}
                  className={`skills-segment-btn ${sourceFilter === seg ? 'active' : ''}`}
                  onClick={() => setSourceFilter(seg)}
                >
                  {seg === 'all' ? 'Alla' : seg === 'skills' ? 'Skills' : seg === 'agents' ? 'Agenter' : 'MCP'}
                  <span className="skills-segment-count">{sourceCounts[seg]}</span>
                </button>
              ))}
            </div>

            {/* ─── Search ─── */}
            <div className="alex-skills-search">
              <Search size={14} />
              <input
                type="text"
                placeholder="Sök capabilities..."
                value={skillSearch}
                onChange={e => setSkillSearch(e.target.value)}
              />
            </div>

            {/* ─── Card Grid ─── */}
            <div className="skills-card-scroll">
              {categoryGroups.map(group => (
                <div key={group.key} className="skills-category-section">
                  <div className="skills-category-label">
                    {group.icon}
                    <span>{group.label}</span>
                    <span className="skills-category-count">{group.items.length}</span>
                  </div>
                  <div className="skills-card-grid">
                    {group.items.map(skill => (
                      <button
                        key={skill.id}
                        className={`skills-card ${selectedSkill?.id === skill.id ? 'active' : ''}`}
                        onClick={() => handleSkillClick(skill)}
                      >
                        <div className="skills-card-icon">
                          {CATEGORY_ICONS[skill.category] || <Puzzle size={14} />}
                        </div>
                        <div className="skills-card-body">
                          <span className="skills-card-name">{skill.name}</span>
                          <span className="skills-card-desc">{skill.description}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {filteredSkills.length === 0 && (
                <div className="alex-panel-empty">
                  <Search size={24} strokeWidth={1.5} />
                  <p>Inga resultat</p>
                </div>
              )}
            </div>

            {/* ─── Skill Detail Overlay ─── */}
            {selectedSkill && (
              <div className="alex-skill-detail-overlay" onClick={() => setSelectedSkill(null)}>
                <div
                  className="alex-skill-detail"
                  onClick={e => e.stopPropagation()}
                >
                  <button className="alex-skill-detail-close" onClick={() => setSelectedSkill(null)} aria-label="Stäng">
                    <X size={16} />
                  </button>
                  <div className="alex-skill-detail-header">
                    {CATEGORY_ICONS[selectedSkill.category]}
                    <h3>{selectedSkill.name}</h3>
                  </div>
                  <p className="alex-skill-detail-desc">{selectedSkill.description}</p>
                  <div className="alex-skill-detail-meta">
                    <span className="alex-skill-detail-badge">
                      {CATEGORY_LABELS[selectedSkill.category]}
                    </span>
                    {selectedSkill.source && (
                      <span className="alex-skill-detail-badge source">
                        {selectedSkill.source === 'subagent' ? 'Agent' : selectedSkill.source === 'mcp' ? 'MCP' : 'Skill'}
                      </span>
                    )}
                  </div>
                  <button className="alex-skill-run-btn">
                    <ArrowUpRight size={14} />
                    Kör
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'costs' && (
          <div className="alex-panel-glass">
            <div className="alex-panel-header">
              <Wallet size={16} />
              <span>Kostnadsöversikt</span>
            </div>
            {/* CostCenter fanns färdigbyggd mot /api/v1/costs men importerades ingenstans.
                Platshållaren "kommer snart" stod framför en fungerande vy. */}
            <Suspense fallback={<div className="alex-panel-empty"><p>Laddar kostnader…</p></div>}>
              <CostCenter />
            </Suspense>
          </div>
        )}
      </section>

      {/* ─── Rollformulär (D&D-stil) ─── */}
      <CharacterSheet
        agentId={sheetOpen ? 'main' : null}
        live={{
          status: gateway.status === 'connected' ? 'Jobbar' : 'Ej ansluten',
        }}
        onClose={() => setSheetOpen(false)}
      />
    </div>
  );
}
